import React, { useEffect, useMemo, useState } from 'react';
import { Form, SonForm, useForm } from '../../../_lib/Form2';
import { SVGIcon } from '../../atoms/SVG';
import { BtnModifier } from '../../molecules/Btn';
import { InteractionStatus } from '../../../types/InteractionStatus';
import { sendLiveRegionMessage } from '../../atoms/LiveRegionGlobal/LiveRegionGlobal';
import { useInvalidateRefreshableBlock } from '../../../_lib/BlockFetcher';
import { FavoriteButtonInteractions } from './partials/FavoriteButtonInteractions';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { Form2Modifier } from '../../../_lib/Form2/Form';

const additionalLocalStatus = {
  FAVORITE: 'FAVORITE',
  NO_FAVORITE: 'NO_FAVORITE',
};

export type FavoriteButtonProps = {
  id: string;
  favorite: { form: SonForm<any>; active: boolean };
  translationNamespace: 'Announcement' | 'PageInteractions' | 'PersonInteraction';
  favoriteText: string;
  noFavoriteText: string;
  svgspriteId: SVGIcon;
  color:
    | BtnModifier.MINOR_ACTION_ACTIVATED_DARK
    | BtnModifier.MINOR_ACTION_ACTIVATED_PETROL
    | BtnModifier.MINOR_ACTION_ACTIVATED_RED;
  resourceToInvalidate?: 'favoritedpages' | 'favoritedusers';
  onSuccess?: () => void;
};

export function FavoriteButton(props: FavoriteButtonProps) {
  const { t } = useSonTranslation(props.translationNamespace);
  const [favorite, setFavorite] = useState(props.favorite);
  const [currentStatus, setCurrentStatus] = useState(
    props.favorite.active ? additionalLocalStatus.FAVORITE : additionalLocalStatus.NO_FAVORITE
  );
  const invalidateRefreshableBlock = useInvalidateRefreshableBlock();

  const { formProps, fetchState } = useForm({
    form: favorite.form,
    onSuccess: props.onSuccess,
  });

  const statusMapping = useMemo(
    () => ({
      [InteractionStatus.SENDING]: t('favoriteButtonForm.status.sending'),
      [InteractionStatus.FAIL]: t('favoriteButtonForm.failure.statusMessage'),
      [additionalLocalStatus.NO_FAVORITE]: t('favoriteButtonForm.success.deleted'),
      [additionalLocalStatus.FAVORITE]: t('favoriteButtonForm.success.added'),
    }),
    [t]
  );

  const getInteractionStatus = () => {
    if (fetchState.type === 'loading') {
      return InteractionStatus.SENDING;
    }
    if (fetchState.type === 'error') {
      return InteractionStatus.FAIL;
    }
    return InteractionStatus.SUCCESS;
  };

  /**
   * Special update as we need to watch for props changes outside from events.
   * This is for displaying the loading spinner on state change for microtransaction.
   *
   * Needs refactoring, as the pageinteractionsWatchButton is the same
   */
  useEffect(() => {
    // check is valid as it may come from Event
    setFavorite(props.favorite);
    setCurrentStatus(props.favorite.active ? additionalLocalStatus.FAVORITE : additionalLocalStatus.NO_FAVORITE);
  }, [props.favorite]);

  useEffect(() => {
    switch (fetchState.type) {
      case 'loading':
        sendLiveRegionMessage(statusMapping[InteractionStatus.SENDING]);
        break;
      case 'success': {
        if (props.resourceToInvalidate) {
          invalidateRefreshableBlock(props.resourceToInvalidate);
        }
        const statusOfResponse = fetchState.response.data.active
          ? additionalLocalStatus.FAVORITE
          : additionalLocalStatus.NO_FAVORITE;
        sendLiveRegionMessage(statusMapping[statusOfResponse]);
        setFavorite(fetchState.response.data);
        setCurrentStatus(statusOfResponse);
        break;
      }
      case 'error':
        sendLiveRegionMessage(statusMapping[InteractionStatus.FAIL]);
        break;
    }
  }, [
    fetchState,
    invalidateRefreshableBlock,
    props.resourceToInvalidate,
    statusMapping,
    t,
    props.translationNamespace,
  ]);

  return (
    <div className="page-interactions__button-container">
      <Form modifier={Form2Modifier.NO_PADDING} key={currentStatus} id={props.id} {...formProps}>
        <FavoriteButtonInteractions
          active={favorite.active}
          svgspriteId={props.svgspriteId}
          color={props.color}
          favoriteText={props.favoriteText}
          noFavoriteText={props.noFavoriteText}
          translationNamespace={props.translationNamespace}
          currentInteractionStatus={getInteractionStatus()}
        />
      </Form>
    </div>
  );
}
